import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import PageNav from '../components/PageNav'
import { to, toWithSender } from '../lib/nav'

import FaxRoutingSection from './FaxRouting'

import {
  ApiError,
  type Assignment,
  type AssignmentOption,
  type AssignmentOptions,
  type AssignmentSaved,
  type AssignmentValues,
  apiFetch,
  getAssignment,
  saveAssignment,
} from '../lib/api'

// Study Information — the assignment hub.
//
// Behavioural specification: `oldcode/StudyInformation.aspx(.vb)`.
//
// The read-only panels are the study as it stands; the Assignment fieldset is
// the editable half, and is what `SaveStudy()` (line 625) writes. The two
// status rules the screen exists to apply — a changed reading physician sends
// the study to Pending, and so does any save of an Examined study — are
// enforced by the server and warned about here before the user commits.
//
// Three legacy behaviours deliberately NOT reproduced:
//   * its `Page_Load` writes on a GET — recomputing patient age from today,
//     persisting it, and in two places transitioning study status. Opening a
//     study here reads and nothing else.
//   * `SaveStudy()` rewrites every column on every save, so merely opening and
//     saving rewrote demographics. Only changed fields are sent.
//   * the required-field validators. The legacy blocks Save until every
//     dropdown is off its "Select a …" entry, which would make a partial
//     assignment — the normal case — unsaveable.
//
// The "← Worklist" link carries the query string forward, so filter, sort and
// page survive the round trip; Cancel and Save and continue do the same.

interface Study {
  id: number
  patient_id: string | null; patient_name: string | null
  patient_birth_date: string | null; patient_age: number | null
  patient_age_note: string | null; patient_sex: string | null
  patient_height: string | null; patient_weight: string | null
  study_date: string | null; study_datetime: string | null
  study_description: string | null; study_room: string | null
  study_type: string | null; accession_number: string | null
  study_instance_uid: string | null; status: string | null
  study_facility: string | null; reading_facility: string | null
  referring_facility: string | null; reading_physician: string | null
  referring_physician: string | null; technician: string | null
  study_report_id: number | null; has_report: boolean
}

const PENDING = 4

// The dropdowns hold ids and the text boxes hold strings; splitting them is
// what lets the diff compare each the way the server does.
const ID_FIELDS = [
  'reading_facility_id', 'reading_physician_id', 'reviewing_physician_id',
  'technician_id', 'study_type_id', 'study_facility_id',
] as const
const TEXT_FIELDS = [
  'study_room', 'patient_height', 'patient_weight', 'patient_sex',
  'patient_comments',
] as const

type IdField = (typeof ID_FIELDS)[number]
type TextField = (typeof TEXT_FIELDS)[number]
type Draft = Record<IdField, number> & Record<TextField, string>

const LABELS: Record<IdField | TextField | 'status_id', string> = {
  reading_facility_id: 'Reading Facility',
  reading_physician_id: 'Reading Physician',
  reviewing_physician_id: 'Reviewing Physician',
  technician_id: 'Technician',
  study_type_id: 'Type of Study',
  study_facility_id: 'Study Location',
  study_room: 'Room #',
  patient_height: 'Height',
  patient_weight: 'Weight',
  patient_sex: 'Sex',
  patient_comments: 'Comments',
  status_id: 'Status',
}

function draftFrom(values: AssignmentValues): Draft {
  const d = {} as Draft
  // 0 is the legacy's "nothing selected" — NULL and 0 are the same thing here,
  // which is how `SaveStudy` writes an untouched dropdown.
  for (const f of ID_FIELDS) d[f] = Number(values[f] ?? 0)
  for (const f of TEXT_FIELDS) d[f] = values[f] ?? ''
  return d
}

// Only what the user actually changed, compared the way the server compares:
// blank and NULL are one value.
function diff(base: Draft, draft: Draft) {
  const patch: Partial<Record<IdField | TextField, string | number | null>> = {}
  for (const f of ID_FIELDS) {
    if (draft[f] !== base[f]) patch[f] = draft[f]
  }
  for (const f of TEXT_FIELDS) {
    const now = draft[f].trim()
    if (now !== base[f].trim()) patch[f] = now === '' ? null : now
  }
  return patch
}

export default function StudyInformationPage() {
  const { studyId } = useParams()
  const id = Number(studyId)
  const [params] = useSearchParams()
  const navigate = useNavigate()

  const [study, setStudy] = useState<Study | null>(null)
  const [assignment, setAssignment] = useState<Assignment | null>(null)
  const [options, setOptions] = useState<AssignmentOptions | null>(null)
  const [base, setBase] = useState<Draft | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)

  const [error, setError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [result, setResult] = useState<AssignmentSaved | null>(null)
  const [saving, setSaving] = useState(false)
  const [rebinding, setRebinding] = useState(false)

  const load = useCallback(async () => {
    const [s, a] = await Promise.all([
      apiFetch<Study>(`/api/v1/studies/${id}`),
      getAssignment(id),
    ])
    setStudy(s)
    setAssignment(a)
    setOptions(a.options)
    setBase(draftFrom(a.values))
    setDraft(draftFrom(a.values))
  }, [id])

  useEffect(() => {
    if (!Number.isFinite(id)) return
    setError(null)
    load().catch((e) => setError(e instanceof ApiError ? e.message : String(e)))
  }, [id, load])

  const back = to('/', params)

  function set<K extends IdField | TextField>(field: K, value: Draft[K]) {
    setDraft((d) => (d ? { ...d, [field]: value } : d))
    setResult(null)
    setSaveError(null)
  }

  // `ddlReadingFacility_SelectedIndexChanged` (line 748) does exactly one
  // thing: rebind the physician list, scoped to the facility just chosen. A
  // selection that survives the rebind is kept; one that does not is reset to
  // the "Select a Physician" entry, which is where `SelectedIndex = 0` leaves
  // it in `BindReadingPhysicianCollection`.
  async function changeReadingFacility(facilityId: number) {
    set('reading_facility_id', facilityId)
    setRebinding(true)
    try {
      const fresh = await getAssignment(id, facilityId || undefined)
      setOptions(fresh.options)
      const ids = new Set(fresh.options.reading_physicians.map((p) => p.id))
      setDraft((d) =>
        d
          ? {
              ...d,
              reading_physician_id: ids.has(d.reading_physician_id) ? d.reading_physician_id : 0,
              reviewing_physician_id: ids.has(d.reviewing_physician_id) ? d.reviewing_physician_id : 0,
            }
          : d,
      )
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setRebinding(false)
    }
  }

  // `btnSave_Click` saves and stays; `btnNext_Click` saves and goes to the
  // report. Same save either way.
  async function save(andContinue: boolean) {
    if (!base || !draft) return
    setSaving(true)
    setSaveError(null)
    setResult(null)
    try {
      const res = await saveAssignment(id, diff(base, draft))
      if (andContinue) {
        navigate(`${res.next}?${params.toString()}`)
        return
      }
      setResult(res)
      // The write is already committed; a failed refresh must not be reported
      // as a failed save.
      try {
        await load()
      } catch {
        /* the page keeps showing the pre-save read until the next visit */
      }
    } catch (e) {
      setSaveError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (!Number.isFinite(id)) {
    return <main className="page"><p className="banner banner-error">Invalid study id.</p></main>
  }
  if (error) {
    return (
      <main className="page">
        <p className="banner banner-error">{error}</p>
        <Link to={back}>Back to worklist</Link>
      </main>
    )
  }
  if (!study || !assignment || !options || !base || !draft) {
    return <main className="page"><p className="muted">Loading…</p></main>
  }

  const statusName = (sid: number | null) =>
    options.statuses.find((s) => s.id === sid)?.name ?? (sid === null ? '—' : `#${sid}`)

  // Warn before the transition, not after it. Either rule can fire; the second
  // fires even when nothing else on the form changed.
  const physicianChanged = draft.reading_physician_id !== base.reading_physician_id
  const willPend =
    assignment.status_id !== PENDING && (assignment.will_move_to_pending || physicianChanged)
  const pendReason = assignment.will_move_to_pending
    ? `Saving this ${statusName(assignment.status_id)} study moves it to Pending.`
    : 'Changing the reading physician moves this study to Pending.'

  const pending = diff(base, draft)
  const dirty = Object.keys(pending).length > 0

  return (
    <main className="page page-wide">
      {/* `StudyInformation.aspx:17` — Log Off alone. The legacy leaves this
          page by its buttons, not by its header: Cancel to the list (`:794`)
          and Save & Proceed to the report (`:790`). */}
      <PageNav strip="studyInformation" studyId={study.id} />

      <h1>Study Information</h1>

      {saveError && (
        <p className="banner banner-error">
          <strong>Not saved.</strong> {saveError}
        </p>
      )}
      {result && <SaveSummary result={result} options={options} statusName={statusName} />}
      {willPend && (
        <p className="banner banner-warn">
          <strong>Status will change.</strong> {pendReason}
        </p>
      )}

      <div className="cols">
        <fieldset>
          <legend>Patient</legend>
          <Field label="ID #" value={study.patient_id} />
          <Field label="Name" value={study.patient_name} />
          <Field label="Date of Birth" value={fmtDate(study.patient_birth_date)} />
          <Field
            label="Age"
            value={study.patient_age === null ? '' : String(study.patient_age)}
            note={study.patient_age_note ?? 'Derived at the study date, not today.'}
          />
          <Field label="Sex" value={study.patient_sex} />
          <Field label="Height" value={study.patient_height} />
          <Field label="Weight" value={study.patient_weight} />
        </fieldset>

        <fieldset>
          <legend>Study</legend>
          <Field label="Study Date" value={fmtDate(study.study_date)} />
          <Field label="Transfer Date" value={fmtStamp(study.study_datetime)} />
          <Field label="Study Type" value={study.study_type} />
          <Field label="Status" value={study.status} />
          <Field label="Description" value={study.study_description} />
          <Field label="Room" value={study.study_room} />
          <Field label="Accession #" value={study.accession_number} />
          <Field label="Study Instance UID" value={study.study_instance_uid} mono />
        </fieldset>

        <fieldset>
          <legend>Facilities and staff</legend>
          <Field label="Study Facility" value={study.study_facility} />
          <Field label="Reading Facility" value={study.reading_facility} />
          <Field label="Referring Facility" value={study.referring_facility} />
          <Field label="Interpreting MD" value={study.reading_physician} />
          <Field label="Referring Physician" value={study.referring_physician} />
          {/* `StudyInformation.aspx:87` — `btnAddPhysician` sits directly under
              the referring-physician dropdown, in the body, not in the header
              strip, and `:740` leaves for
              `AddPhysician.aspx?SENDER=StudyInformation.aspx?STUDYID=<id>`: add
              a physician without losing the study you are assigning. Same round
              trip, with the tab named because one page now serves the four
              screens the legacy kept apart. */}
          <div className="ro-field">
            <span className="ro-label" />
            <span className="ro-value">
              <Link
                to={toWithSender(
                  '/reference',
                  new URLSearchParams({ tab: 'referring-physicians' }),
                  to(`/studies/${study.id}`, params),
                )}
              >
                Add New Physician
              </Link>
            </span>
          </div>
          <Field label="Technician" value={study.technician} />
        </fieldset>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void save(false)
        }}
      >
        <div className="cols" style={{ gridTemplateColumns: '1fr', marginTop: '1rem' }}>
          <fieldset>
            <legend>Assignment</legend>
            <div className="assign-grid">
              <Picker
                id="reading-facility"
                label="Reading Facility"
                placeholder="Select a Facility"
                items={options.facilities}
                value={draft.reading_facility_id}
                disabled={saving}
                onChange={(v) => void changeReadingFacility(v)}
                hint={rebinding ? 'Reloading physicians…' : undefined}
              />
              <Picker
                id="reading-physician"
                label="Reading Physician"
                placeholder="Select a Physician"
                items={options.reading_physicians}
                value={draft.reading_physician_id}
                disabled={saving || rebinding}
                onChange={(v) => set('reading_physician_id', v)}
                hint="Scoped to the reading facility, as the legacy binds it."
              />
              <Picker
                id="reviewing-physician"
                label="Reviewing Physician"
                placeholder="Select a Physician"
                items={options.reading_physicians}
                value={draft.reviewing_physician_id}
                disabled={saving || rebinding}
                onChange={(v) => set('reviewing_physician_id', v)}
              />
              <Picker
                id="technician"
                label="Technician"
                placeholder="Select a Technician"
                items={options.technicians}
                value={draft.technician_id}
                disabled={saving}
                onChange={(v) => set('technician_id', v)}
              />
              <Picker
                id="study-type"
                label="Type of Study"
                placeholder="Select a study type"
                items={options.study_types}
                value={draft.study_type_id}
                disabled={saving}
                onChange={(v) => set('study_type_id', v)}
              />
              <Picker
                id="study-facility"
                label="Study Location"
                placeholder="Select a Facility"
                items={options.facilities}
                value={draft.study_facility_id}
                disabled={saving}
                onChange={(v) => set('study_facility_id', v)}
              />

              <div className="field">
                <label htmlFor="study-room">Room #</label>
                <input
                  id="study-room"
                  type="text"
                  value={draft.study_room}
                  disabled={saving}
                  onChange={(e) => set('study_room', e.target.value)}
                />
              </div>

              <div className="field">
                <label htmlFor="patient-height">Height / Weight</label>
                <div className="field-inline">
                  <input
                    id="patient-height"
                    type="text"
                    inputMode="numeric"
                    value={draft.patient_height}
                    disabled={saving}
                    onChange={(e) => set('patient_height', e.target.value)}
                  />
                  <span className="subtle">inches</span>
                  <input
                    id="patient-weight"
                    type="text"
                    inputMode="numeric"
                    maxLength={3}
                    aria-label="Weight"
                    value={draft.patient_weight}
                    disabled={saving}
                    onChange={(e) => set('patient_weight', e.target.value)}
                  />
                  <span className="subtle">lbs.</span>
                </div>
              </div>

              <div className="field">
                <label>Sex</label>
                <div className="field-radios">
                  {sexChoices(base.patient_sex).map((c) => (
                    <label key={c.value}>
                      <input
                        type="radio"
                        name="patient-sex"
                        value={c.value}
                        checked={draft.patient_sex === c.value}
                        disabled={saving}
                        onChange={() => set('patient_sex', c.value)}
                      />
                      {c.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="field field-wide">
                <label htmlFor="patient-comments">Comments</label>
                <textarea
                  id="patient-comments"
                  rows={3}
                  value={draft.patient_comments}
                  disabled={saving}
                  onChange={(e) => set('patient_comments', e.target.value)}
                />
              </div>
            </div>

            <p className="subtle">
              {dirty
                ? `Unsaved: ${Object.keys(pending)
                    .map((f) => LABELS[f as IdField | TextField])
                    .join(', ')}`
                : 'No changes. Saving still applies the status rules above.'}
            </p>
          </fieldset>
        </div>

        <div className="actions">
          <button type="submit" disabled={saving || rebinding}>
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            disabled={saving || rebinding}
            onClick={() => void save(true)}
          >
            Save &amp; Proceed to Report
          </button>
          <button type="button" disabled={saving} onClick={() => navigate(back)}>
            Cancel
          </button>
        </div>
      </form>

      {/* `StudyInformation.aspx:107` — the "Send to Fax" fieldset sits on this
          page, below the assignment fields, not on a page of its own. Data
          capture only: it saves recipients and numbers and sends nothing
          (DEC-013). It keeps its own save because the legacy's fax columns are
          written by `SaveStudy` alongside everything else, but splitting the
          two here means a fax edit cannot silently reassign the study. */}
      <FaxRoutingSection studyId={id} />
    </main>
  )
}

// What actually changed, in the user's words rather than column names. The
// server answers with ids; the option lists turn them back into names.
function SaveSummary({
  result, options, statusName,
}: {
  result: AssignmentSaved
  options: AssignmentOptions
  statusName: (id: number | null) => string
}) {
  const lists: Partial<Record<string, AssignmentOption[]>> = {
    reading_physician_id: options.reading_physicians,
    reviewing_physician_id: options.reading_physicians,
    technician_id: options.technicians,
    study_type_id: options.study_types,
    study_facility_id: options.facilities,
    reading_facility_id: options.facilities,
  }

  const show = (field: string, v: string | number | null) => {
    if (field === 'status_id') return statusName(typeof v === 'number' ? v : null)
    if (v === null || v === '' || v === 0) return '—'
    const list = lists[field]
    if (list) return list.find((o) => o.id === v)?.name ?? `#${v}`
    return String(v)
  }

  const rows = Object.entries(result.changed)
  if (rows.length === 0) {
    return <p className="banner banner-ok">Nothing to save — the study is unchanged.</p>
  }

  return (
    <div className="banner banner-ok">
      <strong>Saved.</strong>
      {result.status_reason && ` ${result.status_reason}`}
      <ul className="save-summary">
        {rows.map(([field, change]) => (
          <li key={field}>
            {LABELS[field as keyof typeof LABELS] ?? field}: {show(field, change.from)} →{' '}
            <strong>{show(field, change.to)}</strong>
          </li>
        ))}
      </ul>
    </div>
  )
}

// Every legacy `Bind*Collection` inserts a leading item with value 0 before the
// rows, and that 0 is what gets stored when nothing is picked. A stored id that
// is not in the list (a hidden physician, or one at another facility) is shown
// rather than silently reset to the first entry.
function Picker({
  id, label, placeholder, items, value, disabled, hint, onChange,
}: {
  id: string
  label: string
  placeholder: string
  items: AssignmentOption[]
  value: number
  disabled?: boolean
  hint?: string
  onChange: (value: number) => void
}) {
  const missing = value !== 0 && !items.some((i) => i.id === value)
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(Number(e.target.value))}
      >
        <option value={0}>{placeholder}</option>
        {missing && <option value={value}>{`#${value} (not in this list)`}</option>}
        {items.map((i) => (
          <option key={i.id} value={i.id}>{i.name}</option>
        ))}
      </select>
      {hint && <p className="hint">{hint}</p>}
    </div>
  )
}

// `radSex` offers M/F/O. Anything else already stored is offered too, so
// opening a study cannot quietly discard a value the legacy put there.
function sexChoices(stored: string) {
  const choices = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: 'O', label: 'Other' },
  ]
  const raw = stored.trim()
  if (raw && !choices.some((c) => c.value === raw)) {
    choices.push({ value: raw, label: `${raw} (as stored)` })
  }
  return choices
}

function Field({
  label, value, note, mono,
}: { label: string; value: string | null; note?: string; mono?: boolean }) {
  return (
    <div className="ro-field">
      <span className="ro-label">{label}</span>
      <span className={mono ? 'ro-value mono' : 'ro-value'}>
        {value || <span className="muted">—</span>}
      </span>
      {note && <span className="ro-note">{note}</span>}
    </div>
  )
}

function fmtDate(v: string | null) {
  return v ? v.slice(0, 10) : ''
}
function fmtStamp(v: string | null) {
  return v ? v.replace('T', ' ').slice(0, 19) : ''
}
